import {
  Input as NextUIInput,
  type InputProps as NextUIInputProps,
} from '@heroui/input';
import { forwardRef } from '@heroui/react';

export interface InputProps extends NextUIInputProps {
  state?: 'valid' | 'invalid' | 'default';
  customSize?: string;
  inputClassName?: string;
  fontSize?: string;
}

const Input = forwardRef(
  ({ state = 'default', size = 'lg', classNames = {}, customSize, inputClassName = '', fontSize = 'text-sm', style = {}, ...rest }: InputProps, ref) => {
    const states = {
      valid:
        'border-green-300 hover:border-green-300 group-data-[focus=true]:border-green-300',
      invalid:
        'border-pure-red-500 hover:border-pure-red-500 group-data-[focus=true]:border-pure-red-500',
      default:
        'border-grey-400 hover:border-blue-500 dark:border-grey-700 dark:bg-transparent group-data-[focus=true]:border-blue-500 group-data-[focus=true]:shadow-[0_0_0_1px_inset_rgba(67,139,247)]',
    }[state];

    const sizes = {
      sm: 'h-8 min-h-8 max-h-8',
      md: 'h-8.75 min-h-8.75 max-h-8.75',
      lg: 'h-9.5 min-h-9.5 max-h-9.5',
    }[size];

    const FONT_SIZE_OPTIONS = {
      'text-xs': '0.75rem',
      'text-sm': '0.875rem',
      'text-base': '1rem',
      'text-lg': '1.125rem',
      'text-xl': '1.25rem',
      'text-2xl': '1.5rem',
      'text-3xl': '1.875rem',
      'text-4xl': '2.25rem',
    } as const;

    return (
      <NextUIInput
        ref={ref}
        {...rest}
        style={{ fontSize: FONT_SIZE_OPTIONS[fontSize as keyof typeof FONT_SIZE_OPTIONS], ...style }}
        variant={rest.readOnly ? 'faded' : 'bordered'}
        classNames={{
          label: 'text-sm font-semibold text-grey-900 dark:text-grey-200 select-none',
          inputWrapper: `${customSize || sizes} border rounded-md shadow-none p-0 bg-white  ${rest.readOnly ? 'border-grey-400 bg-grey-50 dark:bg-grey-700 dark:border-grey-700' : states
          }`,
          input: `${inputClassName} text-sm text-grey-900 dark:text-grey-200 placeholder:text-grey-400 placeholder:dark:text-grey-500 px-2`,
          ...classNames
        }}
      />
    );
  }
);

export default Input;
